// FILE: managedAgentQaCapability.ts
// Purpose: The single reading of "may this agent be sent to operate an
//          environment?", shared by the modal that offers agents, the card that
//          enables the button, and the server that creates the thread.
// Layer: shared runtime utility
// Depends on: @taskin-code/contracts (catalog + types), ./managedAgentMode
// Exports: `QA_REQUIRED_TOOL_IDS`, `hasManagedAgentQaCapability`,
//          `filterQaCapableAgents`, `NO_QA_CAPABLE_AGENT_MESSAGE`.

import { expandManagedAgentToolIds } from "@taskin-code/contracts";
import type { ManagedAgentMode, QaOperationKind } from "@taskin-code/contracts";

import { canRunAsPrimary } from "./managedAgentMode";

/**
 * Why this lives in `shared` and not in the modal that needs it:
 *
 * "Actualizar QA" opens a thread with an agent chosen from a list. If the list
 * and the server disagreed about which agents qualify, the failure would land
 * on the PM — an agent picked from a menu, a thread that starts, and an agent
 * that answers "no tengo esa herramienta" three turns later, having already
 * been told to sync an environment. So the rule is written once and asked by
 * all three: the picker, the card, and the route that creates the thread and
 * **refuses** an agent that does not qualify however it was sent.
 */

/**
 * The minimum an agent needs to carry out a sync end to end.
 *
 * Three tools, and each is there because the operation stops without it:
 *
 * - `qa_env_preflight` — deciding to sync without reading what the branch
 *   brings is the failure mode this whole feature exists to prevent.
 * - `qa_env_sync` — the operation itself.
 * - `qa_env_config_set` — the one thing a machine cannot resolve alone is a new
 *   environment variable, and an agent that finds one and cannot write it can
 *   only stop halfway.
 *
 * Everything else (`qa_env_sql`, snapshots, `qa_env_create`) is useful and not
 * required: an agent without them can still bring an environment up to date.
 */
export const QA_REQUIRED_TOOL_IDS: ReadonlyArray<string> = [
  "taskin_qa:qa_env_preflight",
  "taskin_qa:qa_env_sync",
  "taskin_qa:qa_env_config_set",
];

/**
 * What each operation needs **on top of** the three above.
 *
 * The base rule is not relaxed for any of them: an agent sent to try a pull
 * request still has to be able to read what it is about to merge and to fix a
 * variable the branch introduced. What changes is the tool that carries out the
 * operation the button was pressed for — offering an agent for "prueba el PR
 * 131" that has no `qa_try_pr` is the same failure the base rule exists to
 * prevent, three turns later.
 *
 * `qa_back_to_base` is required by both trial operations, not only by the second
 * one: a trial that cannot be undone is the state F3 is built to avoid, and an
 * agent that can put a pull request on a shared environment and not take it off
 * has left somebody else to clean up.
 */
export const QA_EXTRA_TOOL_IDS_BY_OPERATION: Readonly<
  Record<QaOperationKind, ReadonlyArray<string>>
> = {
  sync: [],
  "reset-data": [],
  clone: [],
  "try-pr": ["taskin_qa:qa_try_pr", "taskin_qa:qa_back_to_base"],
  "back-to-base": ["taskin_qa:qa_back_to_base"],
  // The isolated pair asks for less than the trial pair does, and the asymmetry
  // is deliberate: a trial holds the shared environment until somebody takes it
  // off, so the agent that puts it on must be able to. A preview holds only its
  // own workspace and expires on a sweep, so an agent that can build one and
  // not tear it down leaves nothing anybody is waiting for.
  "preview-create": ["taskin_qa:qa_preview_create"],
  "preview-destroy": ["taskin_qa:qa_preview_destroy"],
};

/** What the button says when nobody qualifies. Same words in every surface. */
export const NO_QA_CAPABLE_AGENT_MESSAGE = "Ningún agente tiene las tools de QA";

/** The shape both the web's agent list and the server's records satisfy. */
export interface QaCapabilityCandidate {
  readonly mode: ManagedAgentMode;
  readonly tools: ReadonlyArray<string>;
}

/**
 * Whether this agent may be sent to operate an environment.
 *
 * Two conditions, and the second has a trap in it:
 *
 * 1. **A person may pick it.** `canRunAsPrimary` — a `subagent` is never
 *    offered in a menu, and this button is a menu.
 * 2. **Its tool selection includes the three above.** An *empty* selection is
 *    not "no tools": it is how the editor stores "I did not want to think about
 *    tools", and the server reads it as unrestricted (see
 *    `readManagedAgentToolSelection`). Treating empty as incapable here would
 *    hide exactly the agents that in fact have every tool.
 *
 * Legacy whole-server ids (`"taskin_qa"`, saved before per-tool selection
 * existed) are expanded first, for the same reason the server expands them:
 * they meant "all of this server's tools" when they were written.
 *
 * The human's permissions are NOT checked here. They are a property of whoever
 * is asking, not of the agent, and they are enforced per call inside the tools
 * themselves — an agent that qualifies for a PM without `p.env.sync` still gets
 * refused by the tool, with a message that names the missing permission.
 */
export const hasManagedAgentQaCapability = (
  agent: QaCapabilityCandidate,
  /**
   * Narrows the rule to one operation, adding what that operation needs. Omit it
   * for "may this agent be offered for environment work at all", which is what
   * the sync button and the generic picker ask.
   */
  operation?: QaOperationKind,
): boolean => {
  if (!canRunAsPrimary(agent.mode)) return false;
  if (agent.tools.length === 0) return true;
  const selected = new Set(expandManagedAgentToolIds(agent.tools));
  const required =
    operation === undefined
      ? QA_REQUIRED_TOOL_IDS
      : [...QA_REQUIRED_TOOL_IDS, ...QA_EXTRA_TOOL_IDS_BY_OPERATION[operation]];
  return required.every((toolId) => selected.has(toolId));
};

/** The agents a QA operation may be handed to, in the order they came in. */
export const filterQaCapableAgents = <A extends QaCapabilityCandidate>(
  agents: ReadonlyArray<A>,
  operation?: QaOperationKind,
): ReadonlyArray<A> => agents.filter((agent) => hasManagedAgentQaCapability(agent, operation));
